/**
 * Unified Socket.io connection manager.
 *
 * Creates a single Manager instance that multiplexes all namespace connections
 * (/realtime, /messaging, /agent) over one WebSocket transport.
 *
 * Usage:
 *   connectSocket()                        // Call once on app load when user is authenticated
 *   getNamespaceSocket('/realtime')        // Get (or create) a namespace socket
 *   disconnectSocket()                     // Tear down on logout
 */
import { Manager } from 'socket.io-client'
import { ref } from 'vue'

let manager = null
const namespaces = new Map()

function readStoredJwt() {
  try {
    const token = JSON.parse(localStorage.getItem('jwt') || 'null')

    return typeof token === 'string' && token ? token : undefined
  } catch (e) {
    return undefined
  }
}
const isConnected = ref(false)

/**
 * Initialize the Socket.io Manager. Call once when the user authenticates.
 * The Manager opens the underlying WebSocket transport immediately.
 */
export function connectSocket() {
  if (manager) return

  manager = new Manager(window.location.origin, {
    withCredentials: true,
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
  })

  manager.on('open', () => {
    isConnected.value = true
  })
  manager.on('close', () => {
    isConnected.value = false
  })
}

/**
 * Get (or create) a namespace socket. The socket is created with autoConnect: false
 * so callers must explicitly call socket.connect() when ready.
 *
 * Returns null if the Manager hasn't been initialized (user not authenticated).
 */
export function getNamespaceSocket(nsp) {
  if (!manager) return null
  if (namespaces.has(nsp)) return namespaces.get(nsp)

  // The same token the API calls carry (plugins/axios.js reads localStorage 'jwt' into the Bearer header), read fresh on
  // every connect and reconnect so the socket is always the same person as the HTTP session — simulation included.
  const socket = manager.socket(nsp, { autoConnect: false, auth: cb => cb({ token: readStoredJwt() }) })
  namespaces.set(nsp, socket)

  return socket
}

/**
 * Disconnect all namespace sockets and tear down the Manager.
 * Call on logout.
 */
export function disconnectSocket() {
  for (const [, s] of namespaces) {
    s.disconnect()
  }
  namespaces.clear()

  if (manager?.engine) {
    manager.engine.close()
  }
  manager = null
  isConnected.value = false
}

/**
 * Reactive transport-level connection status.
 */
export function useSocketStatus() {
  return { isConnected }
}
